/**
 * Sign-in screen.
 *
 * Deliberately generic and unbranded: the tenant theme is not known until after
 * authentication (§0.1). The tagline comes from the screen design.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button, Field, inputClass } from '../components/ui';

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password, tenantSlug.trim() || undefined);
      navigate('/', { replace: true });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="font-display text-2xl font-extrabold text-ink">Wellness Platform</h1>
          <p className="mt-1.5 text-sm text-ink-muted">
            Intelligent. Integrated. Built for better outcomes.
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="glass-prominent mcos-enter rounded-lg2 p-6 space-y-4"
        >
          <Field label="Email" required>
            <input
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={inputClass}
              placeholder="you@clinic.example"
            />
          </Field>

          <Field label="Password" required>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className={inputClass}
              placeholder="••••••••"
            />
          </Field>

          <Field
            label="Clinic"
            hint="Leave blank unless your clinic uses a specific workspace name."
          >
            <input
              type="text"
              value={tenantSlug}
              onChange={(event) => setTenantSlug(event.target.value)}
              className={inputClass}
              placeholder="default"
            />
          </Field>

          {error && (
            <p role="alert" className="text-sm text-red-600 bg-red-50/80 border border-red-200 rounded-control px-3 py-2">
              {error}
            </p>
          )}

          <Button type="submit" loading={busy} className="w-full">
            Sign in
          </Button>

          {/* Q1, "Please add a Sign up option within the sign in page." */}
          <p className="text-center text-[11px] text-ink-muted">
            New to the platform?{' '}
            <Link to="/signup" className="font-semibold text-steel-deep hover:underline">
              Create an account
            </Link>
          </p>
        </form>

        <p className="mt-5 text-center text-[11px] text-ink-muted leading-relaxed">
          This system contains protected health information. Access is logged.
        </p>
      </div>
    </div>
  );
}
